<template>
    <v-card :loading="loading">
        <v-card-title>
            <h3>Import Packages from Excel</h3>
        </v-card-title>
        <v-divider class="ml-4 mr-5" />
        <v-card-text class="pt-6">
            <v-row>
                <v-col cols="12">
                    <v-file-input
                        v-model="selectedFile"
                        label="Select Excel File"
                        accept=".xlsx,.xls"
                        prepend-icon="mdi-file-excel"
                        show-size
                        @change="handleFileChange"
                    />
                </v-col>
            </v-row>
            <v-alert
                v-if="missingColumns.length > 0"
                type="error"
                dense
                text
                class="mt-2"
            >
                <strong>Missing columns:</strong>
                <ul class="mt-1 mb-0 pl-4">
                    <li v-for="col in missingColumns" :key="col">
                        {{ col }}
                    </li>
                </ul>
            </v-alert>
            <v-alert v-if="errorMessage" type="error" dense text class="mt-2">
                {{ errorMessage }}
            </v-alert>
            <div v-if="validationErrors.length > 0" class="mt-4">
                <v-data-table
                    :headers="errorHeaders"
                    :items="validationErrors"
                    class="elevation-0"
                    :mobile-breakpoint="0"
                    disable-pagination
                    hide-default-footer
                    fixed-header
                    :height="$vuetify.breakpoint.mdAndDown ? '30vh' : '25vh'"
                >
                </v-data-table>
            </div>
        </v-card-text>
        <v-card-actions>
            <v-btn
                text
                color="primary"
                @click="downloadTemplate"
                :disabled="loading"
            >
                <v-icon left>mdi-download</v-icon>
                Download Template
            </v-btn>
            <v-spacer />
            <v-btn text @click="close" :disabled="loading">
                Close
            </v-btn>
            <v-btn
                color="primary"
                rounded
                @click="processImport"
                :disabled="!fileValidated || loading"
                :loading="loading"
            >
                Create
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import * as XLSX from 'xlsx/xlsx.mjs'
import API from '@/services/api'

export default {
    name: 'ImportPackageDialog',
    props: {
        packingList: {
            type: Object,
            required: true,
        },
        project: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        loading: false,
        selectedFile: null,
        fileValidated: false,
        missingColumns: [],
        errorMessage: '',
        validationErrors: [],
        errorHeaders: [
            {
                text: 'Package',
                value: 'package',
                sortable: false,
                align: 'right',
            },
            {
                text: 'Work Order',
                value: 'workOrder',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Part Number',
                value: 'partNumber',
                sortable: false,
                align: 'center',
            },
            {
                text: 'Quantity',
                value: 'quantity',
                sortable: false,
                align: 'right',
            },
            { text: 'Error', value: 'error', sortable: false },
        ],
        requiredColumns: [
            'PACKAGE',
            'WO',
            'PART NUMBER',
            'QUANTITY',
            'NOTES',
            'W',
            'H',
            'L',
            'UNIT',
            'CRATE MASS',
        ],
    }),
    methods: {
        async handleFileChange(file) {
            this.fileValidated = false
            this.missingColumns = []
            this.errorMessage = ''
            if (!file) return

            if (!file.name.match(/\.(xlsx|xls)$/i)) {
                this.errorMessage =
                    'Only Excel files (.xlsx, .xls) are supported'
                this.selectedFile = null
                return
            }

            try {
                this.loading = true
                const data = await this.readFileAsArrayBuffer(file)
                const workbook = XLSX.read(data, { type: 'array' })

                if (workbook.SheetNames.length === 0) {
                    throw new Error('Excel file is empty')
                }

                const worksheet = workbook.Sheets[workbook.SheetNames[0]]
                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                })

                if (jsonData.length === 0) {
                    throw new Error('Excel file is empty')
                }

                const headers = jsonData[0].map(h =>
                    h
                        ? h
                              .toString()
                              .trim()
                              .toLowerCase()
                        : ''
                )

                this.missingColumns = this.requiredColumns.filter(
                    col => !headers.includes(col.toLowerCase())
                )

                if (this.missingColumns.length > 0) {
                    this.selectedFile = null
                    return
                }

                // Verify at least one data row has content in the required columns
                const dataRows = jsonData.slice(1)
                const hasContent = dataRows.some(row =>
                    this.requiredColumns.some(col => {
                        const index = headers.indexOf(col.toLowerCase())
                        const value = row[index]
                        return (
                            value !== undefined &&
                            value !== null &&
                            value !== ''
                        )
                    })
                )

                if (!hasContent) {
                    throw new Error(
                        'File has no data. At least one row with content is required'
                    )
                }

                this.fileValidated = true
            } catch (error) {
                this.errorMessage = error.message
                this.selectedFile = null
            } finally {
                this.loading = false
            }
        },
        async processImport() {
            if (!this.selectedFile || !this.fileValidated) return

            try {
                this.loading = true
                this.validationErrors = []
                this.errorMessage = ''
                const data = await this.readFileAsArrayBuffer(this.selectedFile)
                const workbook = XLSX.read(data, { type: 'array' })
                const worksheet = workbook.Sheets[workbook.SheetNames[0]]

                const jsonData = XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    defval: '',
                })

                const headers = jsonData[0].map(h =>
                    h
                        ? h
                              .toString()
                              .trim()
                              .toLowerCase()
                        : ''
                )
                const headerMap = {}
                this.requiredColumns.forEach(col => {
                    const index = headers.indexOf(col.toLowerCase())
                    if (index !== -1) {
                        headerMap[col] = index
                    }
                })

                const packages = {}
                const dataRows = jsonData.slice(1)
                const localErrors = []

                dataRows.forEach((row, rowIndex) => {
                    // Check if row is completely empty (skip empty rows at the end)
                    const isRowEmpty = row.every(
                        cell =>
                            !cell || cell === '' || String(cell).trim() === ''
                    )
                    if (isRowEmpty) {
                        return
                    }

                    const packageNumber = row[headerMap['PACKAGE']]

                    // Validate packageNumber is present
                    if (!packageNumber || packageNumber === '') {
                        localErrors.push({
                            package: `Row ${rowIndex + 2}`,
                            workOrder: '-',
                            partNumber: '-',
                            quantity: '-',
                            error:
                                'Package number is required and must be a number.',
                        })
                        return
                    }

                    const packageKey = String(packageNumber).trim()

                    // Validate packageKey is a valid number
                    if (
                        isNaN(packageKey) ||
                        packageKey === '' ||
                        Number(packageKey) <= 0
                    ) {
                        localErrors.push({
                            package: packageKey || `Row ${rowIndex + 2}`,
                            workOrder: '-',
                            partNumber: '-',
                            quantity: '-',
                            error:
                                'Package number must be a valid number greater than 0.',
                        })
                        return
                    }

                    if (!packages[packageKey]) {
                        packages[packageKey] = {
                            items: [],
                            packageDimensions: null,
                        }
                    }

                    const wo = row[headerMap['WO']]
                    const partNumber = row[headerMap['PART NUMBER']]
                    const quantity = row[headerMap['QUANTITY']]
                    const notes = row[headerMap['NOTES']]
                    const width = row[headerMap['W']]
                    const height = row[headerMap['H']]
                    const length = row[headerMap['L']]
                    const unit = row[headerMap['UNIT']]
                    const crateMass = row[headerMap['CRATE MASS']]

                    const item = {
                        workOrder: String(wo).trim() || '',
                        partNumber: String(partNumber).trim() || '',
                        suggestedQty: Number(quantity) || 0,
                        notes: notes ? String(notes).trim() : '',
                    }

                    packages[packageKey].items.push(item)

                    if (!packages[packageKey].packageDimensions) {
                        packages[packageKey].packageDimensions = {
                            width: Number(width) || 0,
                            height: Number(height) || 0,
                            depth: Number(length) || 0,
                            units: String(unit).trim() || '',
                            mass: crateMass ? Number(crateMass) : 0,
                        }
                    }
                })

                // Check for local validation errors
                if (localErrors.length > 0) {
                    this.validationErrors = localErrors
                    this.errorMessage = 'Validation errors found in Excel file'
                    this.loading = false
                    return
                }

                const {
                    packingList,
                    createdNewPackingList,
                } = await API.importPackages({
                    packingList: this.packingList,
                    packages: packages,
                    projectId: this.project.id,
                })

                this.$emit('import', { packingList, createdNewPackingList })
                this.close()
            } catch (error) {
                // Check if error has validation errors array
                if (error.response?.data && error.response.data.errors) {
                    this.validationErrors = error.response.data.errors
                    this.errorMessage =
                        error.response.data.message ||
                        'Validation errors found during import'
                } else {
                    this.errorMessage =
                        error.message || 'Error processing Excel file'
                    this.validationErrors = []
                }
            } finally {
                this.loading = false
            }
        },
        downloadTemplate() {
            const templateData = [this.requiredColumns]

            const ws = XLSX.utils.aoa_to_sheet(templateData)
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, 'Packages Template')
            XLSX.writeFile(wb, 'package_import_template.xlsx')
        },
        readFileAsArrayBuffer(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onload = e => resolve(e.target.result)
                reader.onerror = reject
                reader.readAsArrayBuffer(file)
            })
        },
        close() {
            this.selectedFile = null
            this.fileValidated = false
            this.missingColumns = []
            this.errorMessage = ''
            this.validationErrors = []
            this.$emit('close')
        },
    },
}
</script>

<style scoped></style>
